import { dateGB, money } from "./kit";

/** The rows both landed-cost pages read (`landed-cost-by-offer` and `landed-cost-vs-purchase` return the same shape). */
export interface LandedRow {
  quote_id: string | null;
  quote_date: string | null;
  item_id: string;
  item_name: string;
  option_id: string;
  option_name: string;
  funnel: string;
  variant: string;
  incoterm: string | null;
  origin_iso: string | null;
  hs_code: string | null;
  destination: string;
  raw_material_gbp_per_kg: number | null;
  duty_component_gbp_per_kg: number | null;
  transport_gbp_per_kg: number | null;
  total_landed_gbp_per_kg: number | null;
  duty_flag: string | null;
  cost_flag: string | null;
  display_basis: string | null;
  duty_unknown: boolean;
  latest_purchase_price_gbp: number | null;
  latest_purchase_date: string | null;
  latest_purchase_supplier: string | null;
}

export interface LandedLine {
  row: LandedRow;
  /** "{option} - {incoterm}", or the option name alone when the offer has no incoterm. */
  label: string;
  raw: number;
  /** Null when the offer has no HS code, so its duty is not known. */
  duty: number | null;
  transport: number;
  /** C1's landed total; null when the duty is unknown. */
  total: number | null;
}

/** How wide (by offer) and how tall (vs purchase) the "duty unknown" marker is drawn, in GBP per kg. */
export const UNKNOWN_DUTY_WIDTH = 0.6;
export const UNKNOWN_DUTY_HEIGHT = 0.5;

export function landedLabel(row: { option_name: string; incoterm: string | null }): string {
  return row.incoterm ? `${row.option_name} - ${row.incoterm}` : row.option_name;
}

/** The origin in force: the chosen one when an offer on the page has it, else none (a stale choice is ignored). */
export function activeOrigin(rows: LandedRow[], origin: string | null): string | null {
  return origin !== null && rows.some((row) => row.origin_iso === origin) ? origin : null;
}

/** The offers to draw: the origin filter is applied here, in the browser. */
export function landedLines(rows: LandedRow[], origin: string | null): LandedLine[] {
  const active = activeOrigin(rows, origin);
  return rows
    .filter((row) => active === null || row.origin_iso === active)
    .map((row) => ({
      row,
      label: landedLabel(row),
      raw: row.raw_material_gbp_per_kg ?? 0,
      duty: row.duty_unknown ? null : (row.duty_component_gbp_per_kg ?? 0),
      transport: row.transport_gbp_per_kg ?? 0,
      total: row.duty_unknown ? null : row.total_landed_gbp_per_kg,
    }));
}

/** By offer: dearest first; an offer with unknown duty is placed by raw material + transport. */
export function byTotalDesc(lines: LandedLine[]): LandedLine[] {
  const key = (line: LandedLine) => line.total ?? line.raw + line.transport;
  return [...lines].sort((a, b) => key(b) - key(a));
}

/** Vs latest purchase: tallest bar first, unknown duty counted as 0. */
export function byStackDesc(lines: LandedLine[]): LandedLine[] {
  const key = (line: LandedLine) => line.raw + (line.duty ?? 0) + line.transport;
  return [...lines].sort((a, b) => key(b) - key(a));
}

/** The full width of the breakdown bars: the longest bar plus 5%. */
export function stackMax(lines: LandedLine[]): number {
  return Math.max(0.01, ...lines.map((line) => line.raw + (line.duty ?? UNKNOWN_DUTY_WIDTH) + line.transport)) * 1.05;
}

export interface Purchase {
  price: number;
  supplier: string | null;
  date: string | null;
}

/** What we last paid for the variant, carried on every row; null when nothing was ever bought. */
export function latestPurchase(row: {
  latest_purchase_price_gbp: number | null;
  latest_purchase_supplier: string | null;
  latest_purchase_date: string | null;
} | undefined): Purchase | null {
  if (!row || row.latest_purchase_price_gbp == null) return null;
  return { price: row.latest_purchase_price_gbp, supplier: row.latest_purchase_supplier, date: row.latest_purchase_date };
}

/** "Latest purchase £4.69 · Coconova (PH) · 12 May 2026". */
export function purchaseLine(purchase: Purchase): string {
  return [`Latest purchase ${money(purchase.price)}`, purchase.supplier, purchase.date ? dateGB(purchase.date) : null]
    .filter(Boolean)
    .join(" · ");
}

export const NO_PURCHASE = "No purchase recorded for this variant.";
